import { useState, useRef } from "react";
import { Trophy, Plus, TrendingUp, Calendar, Flame, Scale, Pencil, Check, X, History, Award, Heart, Link, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { calculateTier, TIER_PROGRESSION, TIER_COLORS, type EvolveTier, ChatMessage } from "@/types/evolve";
import type { PR } from "@/types/evolve";
import { cn } from "@/lib/utils";
import { MyPlans } from "@/components/MyPlans";
import { WeightPRGraph } from "@/components/WeightPRGraph";
import { WeightHistoryModal } from "@/components/WeightHistoryModal";
import { BadgeGrid } from "@/components/BadgeGrid";
import { Badge } from "@/types/badges";
import { WorkoutCalendar } from "@/components/WorkoutCalendar";
import type { WorkoutSessionRecord } from "@/hooks/useWorkoutSessions";
import { NutritionEntry } from "@/components/NutritionLogger";
import { HealthDashboard } from "@/components/HealthDashboard";
import { NutritionHub } from "@/components/NutritionHub";
import { WeeklyNutritionChart } from "@/components/WeeklyNutritionChart";
import type { HealthDataEntry } from "@/hooks/useHealthKit";
import type { DailyNutritionSummary, NutritionLog } from "@/hooks/useNutritionLogs";
import type { NutritionGoalType } from "@/hooks/useProfile";
import { toast } from "sonner";

interface WeightHistoryEntry {
  id: string;
  weight: number;
  logged_at: string;
  notes?: string;
}

interface ProgressTabProps {
  prs: PR[];
  onAddPR: (liftName?: string) => void;
  onEditPR?: (pr: PR) => void;
  chatMessages?: ChatMessage[];
  chatLoading?: boolean;
  isPremium?: boolean;
  onUpgrade?: () => void;
  onStartWorkout?: (planText: string) => void;
  weightHistory?: { date: string; weight: number }[];
  weightHistoryFull?: WeightHistoryEntry[];
  totalWorkouts?: number;
  currentWeight?: number;
  goalWeight?: number;
  onWeightUpdate?: (weight: number) => void;
  onDeleteWeight?: (id: string) => void;
  isBadgeUnlocked?: (badgeId: string) => boolean;
  getBadgeUnlockDate?: (badgeId: string) => Date | null;
  onBadgeClick?: (badge: Badge) => void;
  highlightedCard?: 'workout' | 'nutrition' | null;
  workoutSessions?: WorkoutSessionRecord[];
  // Health sync props
  isHealthAvailable?: boolean;
  isHealthConnected?: boolean;
  healthPlatformName?: string;
  onConnectHealth?: () => void;
  // Health data read functions
  readSteps?: (days?: number) => Promise<HealthDataEntry[] | null>;
  readHeartRate?: (days?: number) => Promise<HealthDataEntry[] | null>;
  readActiveEnergy?: (days?: number) => Promise<HealthDataEntry[] | null>;
  readWeight?: (days?: number) => Promise<HealthDataEntry[] | null>;
  // Nutrition logging
  onLogNutrition?: (entry: NutritionEntry) => Promise<boolean>;
  // Health import
  onImportFromHealth?: () => Promise<void>;
  // Nutrition goals & progress
  nutritionTargets?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    goalType?: NutritionGoalType;
  };
  todayNutrition?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  weekNutrition?: DailyNutritionSummary[];
  userStats?: {
    weight?: number;
    height?: string;
    age?: number;
    sex?: string;
    activityLevel?: string;
  };
  onSaveNutritionGoals?: (targets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    goalType: NutritionGoalType;
  }) => Promise<void>;
  // Meal history
  nutritionLogs?: NutritionLog[];
  onRepeatMeal?: (log: NutritionLog) => Promise<boolean>;
  onDeleteNutritionLog?: (logId: string) => Promise<boolean>;
}

export function ProgressTab({ 
  prs, 
  onAddPR,
  onEditPR,
  chatMessages = [], 
  chatLoading, 
  isPremium = false,
  onUpgrade,
  onStartWorkout,
  weightHistory = [],
  weightHistoryFull = [],
  totalWorkouts = 0,
  currentWeight,
  goalWeight,
  onWeightUpdate,
  onDeleteWeight,
  isBadgeUnlocked,
  getBadgeUnlockDate,
  onBadgeClick,
  highlightedCard,
  workoutSessions = [],
  // Health sync props
  isHealthAvailable = false,
  isHealthConnected = false,
  healthPlatformName = 'Health',
  onConnectHealth,
  readSteps,
  readHeartRate,
  readActiveEnergy,
  readWeight,
  onLogNutrition,
  onImportFromHealth,
  nutritionTargets,
  todayNutrition,
  weekNutrition,
  userStats,
  onSaveNutritionGoals,
  nutritionLogs = [],
  onRepeatMeal,
  onDeleteNutritionLog,
}: ProgressTabProps) {
  const { currentTier, workoutsToNext, progress, skulls } = calculateTier(totalWorkouts);
  const [showAllTiers, setShowAllTiers] = useState(false);
  const [isEditingWeight, setIsEditingWeight] = useState(false);
  const [weightInput, setWeightInput] = useState(currentWeight?.toString() || '');
  const [showWeightHistory, setShowWeightHistory] = useState(false);

  const displayWeight = currentWeight ?? (weightHistory.length > 0 ? weightHistory[weightHistory.length - 1].weight : null);

  const handleWeightSave = () => {
    const newWeight = parseFloat(weightInput);
    if (!isNaN(newWeight) && newWeight > 0 && onWeightUpdate) {
      onWeightUpdate(newWeight);
      setIsEditingWeight(false);
    }
  };

  const handleWeightCancel = () => {
    setWeightInput(displayWeight?.toString() || '');
    setIsEditingWeight(false);
  };

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 pb-36 space-y-6">
      {/* Tier Progress Card */}
      <section className="glass-card rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <Flame className="w-5 h-5 text-primary" />
            Evolution Progress
          </h2>
          <span className="tier-badge">{totalWorkouts} Workouts</span>
        </div>

        <div className="flex items-center gap-4">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${TIER_COLORS[currentTier]} flex items-center justify-center evolve-glow`}>
            <Trophy className="w-8 h-8 text-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-2xl font-bold">{currentTier}</h3>
            {workoutsToNext !== null ? (
              <p className="text-sm text-muted-foreground">
                <span className="text-primary font-medium">{workoutsToNext} workouts</span> until next tier
              </p>
            ) : (
              <p className="text-sm text-primary font-medium flex items-center gap-1">
                {skulls > 0 && <span>💀 x{skulls}</span>} Maximum tier reached!
              </p>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Progress to next tier</span>
            {workoutsToNext !== null && (
              <span className="text-primary font-medium">
                {(() => {
                  const currentTierIndex = TIER_PROGRESSION.findIndex(t => t.tier === currentTier);
                  const currentTierWorkouts = TIER_PROGRESSION[currentTierIndex]?.workoutsRequired || 0;
                  const nextTierWorkouts = TIER_PROGRESSION[currentTierIndex + 1]?.workoutsRequired || 0;
                  const workoutsInTier = totalWorkouts - currentTierWorkouts;
                  const workoutsNeeded = nextTierWorkouts - currentTierWorkouts;
                  return `${workoutsInTier}/${workoutsNeeded} workouts`;
                })()}
              </span>
            )}
          </div>
          <div className="h-3 bg-secondary rounded-full overflow-hidden">
            <div 
              className={`h-full bg-gradient-to-r ${TIER_COLORS[currentTier]} transition-all duration-700 rounded-full`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Tier Timeline */}
        <button 
          onClick={() => setShowAllTiers(!showAllTiers)}
          className="text-xs text-primary hover:underline"
        >
          {showAllTiers ? "Hide" : "Show"} all tiers
        </button>

        {showAllTiers && (
          <div className="space-y-2 pt-2 border-t border-border/50">
            {TIER_PROGRESSION.map((tier, index) => {
              const isCurrentOrPast = totalWorkouts >= tier.workoutsRequired;
              const isCurrent = currentTier === tier.tier;
              return (
                <div 
                  key={tier.tier}
                  className={cn(
                    "flex items-center justify-between py-2 px-3 rounded-lg transition-all",
                    isCurrent && "bg-primary/10 border border-primary/30",
                    !isCurrentOrPast && "opacity-40"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-2 h-2 rounded-full",
                      isCurrentOrPast ? `bg-gradient-to-r ${TIER_COLORS[tier.tier]}` : "bg-muted"
                    )} />
                    <span className={cn(
                      "text-sm",
                      isCurrent && "font-bold text-primary"
                    )}>
                      {tier.tier}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {tier.workoutsRequired} workouts
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Health Sync Status - only show in native app */}
      {isHealthAvailable && (
        <section className="glass-card rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                "w-10 h-10 rounded-xl flex items-center justify-center",
                isHealthConnected 
                  ? "bg-gradient-to-br from-red-500 to-pink-500" 
                  : "bg-secondary"
              )}>
                <Heart className={cn(
                  "w-5 h-5",
                  isHealthConnected ? "text-white" : "text-muted-foreground"
                )} />
              </div>
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5">
                  {isHealthConnected ? (
                    <>
                      Synced with {healthPlatformName}
                      <span className="text-green-500">✓</span>
                    </>
                  ) : (
                    'Health Sync'
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isHealthConnected 
                    ? 'Weight, workouts, nutrition & steps syncing' 
                    : 'Connect for auto-logged workouts'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isHealthConnected && onImportFromHealth && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={onImportFromHealth}
                  className="shrink-0 text-xs"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Import
                </Button>
              )}
              {!isHealthConnected && onConnectHealth && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onConnectHealth}
                  className="shrink-0"
                >
                  <Link className="w-4 h-4 mr-1" />
                  Connect
                </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Health Dashboard - show when connected */}
      {isHealthAvailable && isHealthConnected && readSteps && readHeartRate && readActiveEnergy && (
        <HealthDashboard
          isConnected={isHealthConnected}
          platformName={healthPlatformName}
          onConnect={onConnectHealth}
          readSteps={readSteps}
          readHeartRate={readHeartRate}
          readActiveEnergy={readActiveEnergy}
        />
      )}

      {/* Unified Nutrition Hub - only show if goals are set */}
      {onLogNutrition && (nutritionTargets?.calories || 0) > 0 && (
        <NutritionHub
          consumed={todayNutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 }}
          targets={{
            calories: nutritionTargets?.calories || 0,
            protein: nutritionTargets?.protein || 0,
            carbs: nutritionTargets?.carbs || 0,
            fat: nutritionTargets?.fat || 0,
            goalType: nutritionTargets?.goalType,
          }}
          onLogNutrition={onLogNutrition}
          isHealthConnected={isHealthConnected}
          healthPlatformName={healthPlatformName}
          userStats={userStats}
          onSaveNutritionGoals={onSaveNutritionGoals}
          nutritionLogs={nutritionLogs}
          onRepeatMeal={onRepeatMeal}
          onDeleteNutritionLog={onDeleteNutritionLog}
          weekNutrition={weekNutrition}
        />
      )}

      {/* Weekly Nutrition Chart - shown separately for better visibility */}
      {weekNutrition && weekNutrition.length > 0 && nutritionTargets?.calories && (
        <WeeklyNutritionChart
          data={weekNutrition}
          calorieTarget={nutritionTargets.calories}
        />
      )}

      {/* Current Weight Section */}
      <section className="glass-card rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <Scale className="w-6 h-6 text-foreground" />
            </div>
            {isEditingWeight ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={weightInput}
                  onChange={(e) => setWeightInput(e.target.value)}
                  className="w-24 h-10 text-lg font-display font-bold"
                  autoFocus
                />
                <span className="text-lg font-display">lbs</span>
                <button
                  onClick={handleWeightSave}
                  className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-colors"
                >
                  <Check className="w-4 h-4 text-green-400" />
                </button>
                <button
                  onClick={handleWeightCancel}
                  className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 transition-colors"
                >
                  <X className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => {
                  setWeightInput(displayWeight?.toString() || '');
                  setIsEditingWeight(true);
                }}
                className="group flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                <div>
                  <p className="text-2xl font-display font-bold">
                    {displayWeight && displayWeight > 0 ? `${displayWeight} lbs` : '—'}
                  </p>
                  <p className="text-xs text-muted-foreground">Current Weight</p>
                </div>
                <Pencil className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isEditingWeight && weightHistory.length > 1 && (
              <div className="text-right mr-2">
                {(() => {
                  const latest = weightHistory[weightHistory.length - 1]?.weight || 0;
                  const previous = weightHistory[weightHistory.length - 2]?.weight || 0;
                  const diff = latest - previous;
                  return (
                    <p className={`text-sm font-medium ${diff > 0 ? 'text-red-400' : diff < 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                      {diff > 0 ? '+' : ''}{diff.toFixed(1)} lbs
                    </p>
                  );
                })()}
                <p className="text-xs text-muted-foreground">vs last</p>
              </div>
            )}
            {weightHistoryFull.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowWeightHistory(true)}
                className="h-8 px-2"
              >
                <History className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Weight History Modal */}
      <WeightHistoryModal
        open={showWeightHistory}
        onOpenChange={setShowWeightHistory}
        weightHistory={weightHistoryFull}
        onDelete={onDeleteWeight || (() => {})}
        goalWeight={goalWeight}
      />

      {/* My Plans Section */}
      <MyPlans 
        messages={chatMessages} 
        loading={chatLoading}
        isPremium={isPremium}
        onUpgrade={onUpgrade}
        onStartWorkout={onStartWorkout}
        highlightedCard={highlightedCard}
      />

      {/* PR Tracking - Big 3 Lifts */}
      <section className="glass-card rounded-2xl p-5 space-y-4">
        <div id="pr-section-header" className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Personal Records
          </h2>
          <Button variant="evolve" size="sm" onClick={() => onAddPR()}>
            <Plus className="w-4 h-4" />
            Add PR
          </Button>
        </div>

        {/* Big 3 Lifts Summary */}
        {(() => {
          const benchPR = prs.find(pr => pr.lift.toLowerCase().includes('bench'));
          const squatPR = prs.find(pr => pr.lift.toLowerCase().includes('squat'));
          const deadliftPR = prs.find(pr => pr.lift.toLowerCase().includes('deadlift'));
          
          const big3Total = (benchPR?.weight || 0) + (squatPR?.weight || 0) + (deadliftPR?.weight || 0);
          
          return (
            <div className="space-y-4">
              {/* Big 3 */}
              <div className="bg-secondary/30 rounded-xl p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Big 3 Lifts</p>
                <div className="grid grid-cols-3 gap-3 text-center mb-3">
                  <button 
                    className={`hover:opacity-70 transition-all rounded-lg p-2 ${!benchPR ? 'border border-dashed border-primary/40 hover:border-primary hover:bg-primary/5' : ''}`}
                    onClick={() => benchPR ? onEditPR?.(benchPR) : onAddPR('Bench Press')}
                  >
                    <p className="text-xs text-muted-foreground">Bench</p>
                    <p className={`text-lg font-display font-bold ${benchPR ? 'text-primary' : 'text-muted-foreground/50'}`}>
                      {benchPR?.weight || '+ Add'}
                    </p>
                  </button>
                  <button 
                    className={`hover:opacity-70 transition-all rounded-lg p-2 ${!squatPR ? 'border border-dashed border-primary/40 hover:border-primary hover:bg-primary/5' : ''}`}
                    onClick={() => squatPR ? onEditPR?.(squatPR) : onAddPR('Squat')}
                  >
                    <p className="text-xs text-muted-foreground">Squat</p>
                    <p className={`text-lg font-display font-bold ${squatPR ? 'text-primary' : 'text-muted-foreground/50'}`}>
                      {squatPR?.weight || '+ Add'}
                    </p>
                  </button>
                  <button 
                    className={`hover:opacity-70 transition-all rounded-lg p-2 ${!deadliftPR ? 'border border-dashed border-primary/40 hover:border-primary hover:bg-primary/5' : ''}`}
                    onClick={() => deadliftPR ? onEditPR?.(deadliftPR) : onAddPR('Deadlift')}
                  >
                    <p className="text-xs text-muted-foreground">Deadlift</p>
                    <p className={`text-lg font-display font-bold ${deadliftPR ? 'text-primary' : 'text-muted-foreground/50'}`}>
                      {deadliftPR?.weight || '+ Add'}
                    </p>
                  </button>
                </div>
                <div className="text-center pt-2 border-t border-border/30">
                  <p className="text-xs text-muted-foreground">Big 3 Total</p>
                  <p className="text-2xl font-display font-bold evolve-text-gradient">
                    {big3Total > 0 ? `${big3Total} lbs` : '—'}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {prs.length === 0 ? (
          <div className="text-center py-4">
            <Trophy className="w-10 h-10 text-muted mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">No PRs logged yet</p>
            <p className="text-xs text-muted-foreground mt-1">Log Bench, Squat, and Deadlift to see your total!</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider pb-2 border-b border-border/50">
              <span>Lift</span>
              <span className="text-center">Weight</span>
              <span className="text-right">Date</span>
            </div>
            {prs.map((pr) => (
              <button 
                key={pr.id} 
                className="grid grid-cols-3 gap-2 py-2 text-sm w-full hover:bg-secondary/30 rounded transition-colors text-left"
                onClick={() => onEditPR?.(pr)}
              >
                <span className="font-medium">{pr.lift}</span>
                <span className="text-center text-primary font-bold">{pr.weight} lbs</span>
                <span className="text-right text-muted-foreground text-xs">
                  {new Date(pr.date).toLocaleDateString()}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Weight & PR Progress Graphs */}
      <WeightPRGraph prs={prs} weightHistory={weightHistory} />

      {/* Workout Calendar with Streaks */}
      <WorkoutCalendar sessions={workoutSessions} totalWorkouts={totalWorkouts} />

      {/* Badges Section - moved to bottom */}
      {isBadgeUnlocked && getBadgeUnlockDate && (
        <section className="glass-card rounded-2xl p-5">
          <BadgeGrid
            isBadgeUnlocked={isBadgeUnlocked}
            getBadgeUnlockDate={getBadgeUnlockDate}
            isPremium={isPremium}
            onBadgeClick={onBadgeClick}
          />
        </section>
      )}

      {/* Premium Upgrade CTA */}
      {!isPremium && (
        <button
          onClick={onUpgrade}
          className="w-full glass-card rounded-2xl p-5 hover:scale-[1.02] transition-all duration-300 group border border-primary/20 hover:border-primary/50"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-orange-500 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
              <Award className="w-7 h-7 text-foreground" />
            </div>
            <div className="text-left flex-1">
              <p className="font-display font-bold text-lg group-hover:text-primary transition-colors">
                Unlock Your Full Potential
              </p>
              <p className="text-sm text-muted-foreground">
                Get AI-personalized plans, unlimited tweaks & priority coaching
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-xs text-muted-foreground">Starting at</p>
              <p className="text-lg font-display font-bold text-primary">$0.33/day</p>
            </div>
          </div>
        </button>
      )}
    </div>
  );
}
